import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetData,
  type GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetErrors,
  type GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetResponse,
  getPdpV1PortfoliosPortfolioIdPdpsPdpIdGet,
} from '../../generated';

export type GetPdpQueryVariables = GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetData['path'] & {
  organization_id: string;
};

export type GetPdpQuery = GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetResponse;

export const useGetPdpQuery = <
  TData = GetPdpQuery,
  TError = GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetErrors[keyof GetPdpV1PortfoliosPortfolioIdPdpsPdpIdGetErrors],
>(
  { organization_id, portfolio_id, pdp_id }: GetPdpQueryVariables,
  options?: Omit<UseQueryOptions<GetPdpQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetPdpQuery, TError, TData>({
    queryKey: ['GetPdp', { organization_id, portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getPdpV1PortfoliosPortfolioIdPdpsPdpIdGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetPdpQuery.getKey = ({ organization_id, portfolio_id, pdp_id }: GetPdpQueryVariables) => [
  'GetPdp',
  { organization_id, portfolio_id, pdp_id },
];

useGetPdpQuery.fetcher = ({ portfolio_id, pdp_id }: GetPdpQueryVariables, _headers?: RequestInit['headers']) => {
  return async () => {
    const { data } = await getPdpV1PortfoliosPortfolioIdPdpsPdpIdGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });
    return data;
  };
};

useGetPdpQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}',
};
